import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  OnChanges,
  SimpleChanges,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ProfileService } from '../../services/profile.service';
import { FilterOptions, SortOption, FilterParams } from '../../shared/types';
import { Checkbox } from '../../shared/components/checkbox';
import { ModalFooterActionsComponent } from '../../shared/components/modal-footer-actions/modal-footer-actions.component';
import { buildPackageFilterParams } from '../../shared/utils/package-filter-params.util';

/**
 * Modal component for selecting and applying filters and sorting for package listings (car wash packages).
 * Provides a UI to choose car wash types, package statuses, locations, and sort options, and emits events
 * to apply/clear filters or trigger table sorting. It also loads available locations from the ProfileService.
 * @component
 * @selector app-package-filter-modal
 * @example
 *   <app-package-filter-modal
 *     [isVisible]="isModalOpen"
 *     [currentFilters]="filters"
 *     (applyFilters)="onApply($event)"
 *     (closeModal)="onClose()"
 *     (clearFilters)="onClear()"
 *     (sortTable)="onSort($event)">
 *   </app-package-filter-modal>
 * @input {boolean} isVisible - Controls modal visibility; when set true the component will sync internal selections with currentFilters.
 * @input {FilterOptions | null} currentFilters - Current filter state used to initialize selections (carWashTypes, statuses, locations, sortBy). When null the selections are reset.
 * @output {EventEmitter<void>} closeModal - Emitted when the modal should be closed (used by onClose and onCancel).
 * @output {EventEmitter<FilterOptions>} applyFilters - Emitted with the composed FilterOptions when the user applies filters.
 * @output {EventEmitter<void>} clearFilters - Emitted when the user clears all filters and selections are reset.
 * @output {EventEmitter<SortOption>} sortTable - Emitted with a table sort mapping when a sort option is selected (used to trigger table column sorting).
 *
 * Key methods:
 * - loadLocations(): subscribes to ProfileService.locations$ to populate locationOptions and triggers initial load.
 * - onClose()/onCancel(): emit closeModal to request closing the modal.
 * - onClearFilters(): resets all selections and emits clearFilters.
 * - onApplyFilters(): builds a FilterOptions object from current selections, emits applyFilters, and if a sort is selected maps and emits a SortOption via sortTable.
 * - buildApiParams(): converts current selections into API-friendly FilterParams (handles status flags, wash type format conversion, and locations).
 * - getSortFieldMapping(): returns the mapping between UI sort keys and table column field/order used to generate SortOption payloads.
 * - hasActiveFilters(): returns true if any filter or sort selection is active.
 *
 * Lifecycle hooks:
 * - constructor(ProfileService): injects the ProfileService used to load locations.
 * - ngOnInit(): calls loadLocations to initialize location options.
 * - ngOnChanges(changes: SimpleChanges): when isVisible becomes true and currentFilters exists, synchronizes internal selection state with currentFilters so the modal shows the up-to-date selections.
 */
@Component({
  selector: 'app-package-filter-modal',
  standalone: true,
  imports: [CommonModule, Checkbox, ModalFooterActionsComponent],
  templateUrl: './package-filter-modal.component.html',
  styleUrls: ['./package-filter-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class PackageFilterModalComponent implements OnInit, OnChanges {
  @Input() set isVisible(value: boolean) {
    this._isVisible = value;
  }
  get isVisible(): boolean {
    return this._isVisible;
  }
  private _isVisible = false;

  private _currentFilters: FilterOptions | null = null;

  /** Set the current filter options and synchronize selected filter state; resets selections when null. */
  @Input() set currentFilters(filters: FilterOptions | null) {
    this._currentFilters = filters;
    if (filters) {
      this.selectedCarWashTypes = [...filters.carWashTypes];
      this.selectedStatuses = [...filters.statuses];
      this.selectedLocations = [...filters.locations];
      this.selectedSortBy = filters.sortBy;
    } else {
      // Reset to empty when filters are cleared
      this.selectedCarWashTypes = [];
      this.selectedStatuses = [];
      this.selectedLocations = [];
      this.selectedSortBy = '';
    }
  }

  get currentFilters(): FilterOptions | null {
    return this._currentFilters;
  }

  ngOnChanges(changes: SimpleChanges): void {
    // When modal becomes visible, sync with current filters
    if (
      changes['isVisible'] &&
      changes['isVisible'].currentValue === true &&
      this._currentFilters
    ) {
      this.selectedCarWashTypes = [...this._currentFilters.carWashTypes];
      this.selectedStatuses = [...this._currentFilters.statuses];
      this.selectedLocations = [...this._currentFilters.locations];
      this.selectedSortBy = this._currentFilters.sortBy;
    }
  }

  @Output() closeModal = new EventEmitter<void>();
  @Output() applyFilters = new EventEmitter<FilterOptions>();
  @Output() clearFilters = new EventEmitter<void>();
  @Output() sortTable = new EventEmitter<SortOption>();

  // Filter options
  selectedCarWashTypes: string[] = [];
  selectedStatuses: string[] = [];
  selectedLocations: string[] = [];
  selectedSortBy = '';

  // Available options (can be passed as inputs or fetched from service)
  carWashTypeOptions = [
    { value: 'monthly-membership', label: 'Monthly Membership' },
    { value: 'single-wash', label: 'Single Wash' },
    { value: 'detailing', label: 'Detailing' },
    { value: 'add-on', label: 'Add-on' },
  ];

  statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
  ];

  locationOptions: any[] = [];

  sortByOptions = [
    { value: 'list-price-high-low', label: 'List Price High to Low' },
    { value: 'list-price-low-high', label: 'List Price Low to High' },
    { value: 'strikeoff-price-high-low', label: 'Strike-off Price High to Low' },
    { value: 'strikeoff-price-low-high', label: 'Strike-off Price Low to High' },
    { value: 'family-plan-high-low', label: 'Family Plan Price High to Low' },
    { value: 'family-plan-low-high', label: 'Family Plan Price Low to High' },
    { value: 'recently-updated', label: 'Recently Updated' },
    { value: 'earliest-updated', label: 'Earliest Updated' },
  ];

  constructor(private profileService: ProfileService) {}

  ngOnInit(): void {
    this.loadLocations();
  }

  loadLocations(): void {
    this.profileService.locations$.subscribe(locations => {
      this.locationOptions = locations.map((loc: any) => ({ label: loc.branch, value: loc.id }));
    });
    // Initialize locations load
    this.profileService.loadLocations();
  }

  onToggleSelection(selected: string[], value: string, checked: boolean): void {
    const index = selected.indexOf(value);
    if (checked && index === -1) selected.push(value);
    if (!checked && index !== -1) selected.splice(index, 1);
  }

  onClose() {
    this.closeModal.emit();
  }

  onCancel() {
    this.closeModal.emit();
  }

  onClearFilters() {
    this.selectedCarWashTypes = [];
    this.selectedStatuses = [];
    this.selectedLocations = [];
    this.selectedSortBy = '';
    this.clearFilters.emit();
  }

  /**
   * Apply the currently selected filter options by emitting an applyFilters event and, if a sort option is selected, emit a sortTable event to trigger table column sorting.
   * @example
   * nApplyFilters()
   * undefined
   * @returns {void} Emits filter and optional sort events; does not return a value.
   */
  onApplyFilters() {
    const filters: FilterOptions = {
      carWashTypes: this.selectedCarWashTypes,
      statuses: this.selectedStatuses,
      locations: this.selectedLocations,
      sortBy: this.selectedSortBy,
    };

    // Emit filter application
    this.applyFilters.emit(filters);

    // Handle sorting separately - trigger table column sorting
    if (this.selectedSortBy) {
      const sortMapping = this.getSortFieldMapping();
      const sortOption = sortMapping[this.selectedSortBy];
      if (sortOption) {
        this.sortTable.emit(sortOption);
      }
    }
  }

  /**
   * Build query parameters for the packages API based on the component's selected filters.
   * @example
   * uildApiParams()
   * { package_status: 'true', wash_types: 'single_wash', locations: 'loc1,loc2' }
   * @returns {FilterParams} Filter parameters object for the API (may include package_status, wash_types, and locations).
   **/
  buildApiParams(): FilterParams {
    // Note: sort_by is handled separately by triggering table column sorting
    return buildPackageFilterParams({
      statuses: this.selectedStatuses,
      carWashTypes: this.selectedCarWashTypes,
      locations: this.selectedLocations,
    });
  }

  // Map filter sort options to table column fields
  /**
   * Returns a mapping from external sort identifiers to the corresponding field and sort order.
   * @example
   * etSortFieldMapping()
   * {
   *   'list-price-high-low': { field: 'single_list_price', order: 'desc' },
   *   'list-price-low-high': { field: 'single_list_price', order: 'asc' },
   *   'strikeoff-price-high-low': { field: 'single_strikeoff_price', order: 'desc' },
   *   'strikeoff-price-low-high': { field: 'single_strikeoff_price', order: 'asc' },
   *   'family-plan-high-low': { field: 'monthly_list_price', order: 'desc' },
   *   'family-plan-low-high': { field: 'monthly_list_price', order: 'asc' },
   *   'recently-updated': { field: 'updated_at', order: 'desc' },
   *   'earliest-updated': { field: 'updated_at', order: 'asc' }
   * }
   * @returns {{ [key: string]: { field: string, order: 'asc' | 'desc' } }} Mapping of sort keys to field and order.
   **/
  getSortFieldMapping(): Record<string, { field: string; order: 'asc' | 'desc' }> {
    return {
      'list-price-high-low': { field: 'single_list_price', order: 'desc' },
      'list-price-low-high': { field: 'single_list_price', order: 'asc' },
      'strikeoff-price-high-low': { field: 'single_strikeoff_price', order: 'desc' },
      'strikeoff-price-low-high': { field: 'single_strikeoff_price', order: 'asc' },
      'family-plan-high-low': { field: 'monthly_list_price', order: 'desc' },
      'family-plan-low-high': { field: 'monthly_list_price', order: 'asc' },
      'recently-updated': { field: 'updated_at', order: 'desc' },
      'earliest-updated': { field: 'updated_at', order: 'asc' },
    };
  }

  hasActiveFilters(): boolean {
    return (
      this.selectedCarWashTypes.length > 0 ||
      this.selectedStatuses.length > 0 ||
      this.selectedLocations.length > 0 ||
      this.selectedSortBy !== ''
    );
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
